import { useMutation } from "@apollo/client";
import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { useLocation } from "react-router";

import DirectionButton from "@components/buttons/DirectionButton";
import PurchaseHours from "@components/PurchaseHours";
import AvailabilitySelect from "@components/schedule/AvailabilitySelect";
import Booked from "@components/schedule/Booked";
import ConfirmForm from "@components/schedule/ConfirmForm";
import ContactMethod from "@components/schedule/ContactMethod";
import Duration from "@components/schedule/Duration";
import styles from "@components/schedule/ScheduleSession.module.css";
import {
  AnalyticsEventCreateDocument,
  AnalyticsEventCreateMutation,
  AnalyticsEventCreateMutationVariables,
  ConsultFragment as Consult,
  ConsultAvailabilityFragment as ConsultAvailability,
  ConsultDurationEnum,
  ContactMethodEnum,
  GetJourneyWithExpertAndConsultsQuery
} from "@typing/Generated";
import { generateHTMLId } from "@utils/htmlUtils";

type Props = {
  defaultDate?: Date | null;
  journey: GetJourneyWithExpertAndConsultsQuery["journey"];
};

enum Steps {
  AVAILABILITY_SELECT = "AVAILABILITY_SELECT",
  BOOKED = "BOOKED",
  DURATION = "DURATION",
  CONFIRM = "CONFIRM",
  CONTACT_METHOD = "CONTACT_METHOD"
}

const ScheduleSession = ({ defaultDate, journey }: Props) => {
  const location = useLocation();
  const [bookedConsult, setBookedConsult] = useState<Consult>();
  const [duration, setDuration] = useState<ConsultDurationEnum | undefined>();
  const [contactMethod, setContactMethod] = useState<ContactMethodEnum | undefined>();
  const [availability, setAvailability] = useState<ConsultAvailability | undefined>();
  const [schedulingAttemptId] = useState<string>(generateHTMLId());

  const [createAnalyticsEvent] = useMutation<AnalyticsEventCreateMutation, AnalyticsEventCreateMutationVariables>(
    AnalyticsEventCreateDocument
  );

  const durationRef = useRef<HTMLDivElement>(null);
  const availabilityRef = useRef<HTMLDivElement>(null);
  const contactMethodRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLDivElement>(null);

  const scrollToNextSection = (step: Steps) => {
    if (step === Steps.DURATION) {
      setTimeout(() => durationRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 500);
    } else if (step === Steps.AVAILABILITY_SELECT) {
      setTimeout(() => availabilityRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 500);
    } else if (step === Steps.CONTACT_METHOD) {
      setTimeout(() => contactMethodRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 500);
    } else if (step === Steps.CONFIRM) {
      setTimeout(() => confirmRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 500);
    }
  };

  const handleEditBooking = () => {
    scrollToNextSection(Steps.DURATION);
  };

  const handleDurationSelected = (newDuration: ConsultDurationEnum) => {
    createAnalyticsEvent({
      variables: {
        eventData: {
          duration: newDuration,
          page: location.pathname,
          schedulingAttemptId,
          step: Steps.DURATION,
          type: "scheduleSession"
        }
      }
    });

    setDuration(newDuration);
    scrollToNextSection(Steps.AVAILABILITY_SELECT);
  };

  const handleContactMethodSelected = (contactMethod: ContactMethodEnum) => {
    createAnalyticsEvent({
      variables: {
        eventData: {
          contactMethod,
          page: location.pathname,
          schedulingAttemptId,
          step: Steps.CONTACT_METHOD,
          type: "scheduleSession"
        }
      }
    });

    setContactMethod(contactMethod);
    scrollToNextSection(Steps.CONFIRM);
  };

  const handleAvailabilitySelected = (newAvailability?: ConsultAvailability) => {
    createAnalyticsEvent({
      variables: {
        eventData: {
          availability: newAvailability,
          page: location.pathname,
          schedulingAttemptId,
          step: Steps.AVAILABILITY_SELECT,
          type: "scheduleSession"
        }
      }
    });

    setAvailability(newAvailability);
    if (newAvailability) {
      scrollToNextSection(Steps.CONTACT_METHOD);
    }
  };

  const handleConsultBooked = (consult: Consult) => {
    createAnalyticsEvent({
      variables: {
        eventData: {
          consult,
          page: location.pathname,
          schedulingAttemptId,
          step: Steps.BOOKED,
          type: "scheduleSession"
        }
      }
    });

    setBookedConsult(consult);
    scrollToNextSection(Steps.BOOKED);
  };

  return (
    <div>
      {!bookedConsult && (
        <div className={styles.goBack}>
          <DirectionButton direction="back" />
        </div>
      )}

      {!journey.hasExpertAccess ? (
        <PurchaseHours journeyId={journey.id} />
      ) : bookedConsult ? (
        <Booked journey={journey} />
      ) : (
        <div>
          <div ref={durationRef}>
            <motion.div exit={{ y: "-200%" }} key="sessions-duration" transition={{ duration: 0.4, ease: "easeIn" }}>
              <Duration duration={duration} onDurationSelected={handleDurationSelected} />
            </motion.div>
          </div>
          {duration && (
            <div className={styles.section} ref={availabilityRef}>
              <AvailabilitySelect
                availability={availability}
                defaultDate={defaultDate}
                duration={duration}
                journey={journey}
                onAvailabilitySelected={handleAvailabilitySelected}
                schedulingAttemptId={schedulingAttemptId}
              />
            </div>
          )}
          {duration && availability && (
            <div className={styles.section} ref={contactMethodRef}>
              <ContactMethod contactMethod={contactMethod} onContactMethodSelected={handleContactMethodSelected} />
            </div>
          )}
          {duration && availability && contactMethod && (
            <div className={styles.section} ref={confirmRef}>
              <ConfirmForm
                availability={availability}
                contactMethod={contactMethod}
                duration={duration}
                handleEditBooking={handleEditBooking}
                journey={journey}
                onSuccess={handleConsultBooked}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScheduleSession;
